import {useThemeColors} from '@nfq/colors';


import {convertFileSize} from 'UI/components/action/inputs/FileCard/utils';
import {useFormField} from 'UI/components/action/inputs/hooks/useFormField';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * Custom hook `useFileCard` containing all the logic for the `FileCard` component.
 * This hook manages file display data, handles file deletion callbacks, and provides styled theme colors.
 * It automatically registers the file with the form field system and processes file information for display purposes.
 *
 * @param file     The File object containing file data, name, and size information.
 * @param index    The numeric index of the file in the file list for identification purposes.
 * @param validate Validation function to validate the file input.
 * @param onDelete Optional callback function that will be invoked when the file should be deleted.
 * @returns An object with the custom hook properties including colors, delete handler, file name, and formatted size.
 *
 * @example
 * ```tsx
 * const fileCardHook = useFileCard(
 *     selectedFile,
 *     0,
 *     (index) => removeFileAtIndex(index)
 * );
 * ```
 */
export const useFileCard = (
    file: File,
    index: number,
    validate: ValidationFunc,
    onDelete?: (index: number) => void
) => {
    const {error} = useFormField<File, HTMLInputElement>(`uploadedFile-${index}`, file, file, true, validate);
    const colors = useThemeColors();

    /**
     * Handles the deletion of the file by invoking the onDelete callback with the current file index.
     * This function safely calls the optional onDelete callback if it exists, preventing errors when no callback is provided.
     * The callback receives the file index to identify which specific file should be removed from the collection.
     */
    const handleDelete = () => {
        onDelete?.(index);
    };

    return {
        colors,
        error,
        handleDelete,
        name: file.name,
        size: convertFileSize(file.size)
    };
};